import { Check, ExternalLink, Loader2, Upload, X } from "lucide-react";
import { type ChangeEvent, useCallback, useRef, useState } from "react";
import { useSignedUrl } from "../../hooks/useSignedUrl";
import { isDataUrl, isS3Key, uploadFileToS3 } from "../../utils/fileUpload";
import { notifications } from "../../utils/notifications";

export interface FileUploadFieldProps {
	/** Unique id for the hidden file input (also used in htmlFor). */
	id: string;
	/** Display label for the upload area. */
	label: string;
	/** When true, shows a "Required" badge. */
	required?: boolean;
	/** Comma-separated accepted file extensions (e.g. ".pdf,.jpg,.jpeg,.png"). */
	accept?: string;
	/** Current stored value: S3 key, data URL, or undefined. */
	value: string | undefined;
	/** Called with the S3 key after a successful upload. */
	onUpload: (key: string) => void;
	/** Called when the user removes the uploaded file. */
	onRemove: () => void;
	/** Optional description text below the label. */
	description?: string;
	/** When true, shows only the compact inline variant (no card wrapper). */
	compact?: boolean;
}

/**
 * Reusable file upload field that uploads to S3 via the BFF.
 * Renders the same visual pattern used throughout the KYB step components:
 * - Empty state: styled "Choose File" button
 * - Uploading: spinner
 * - Uploaded: green success bar with remove button and optional "View" link
 *
 * Supports backward compatibility with legacy base64 data URLs.
 */
export function FileUploadField({
	id,
	label,
	required,
	accept = ".pdf,.jpg,.jpeg,.png",
	value,
	onUpload,
	onRemove,
	description,
	compact,
}: FileUploadFieldProps) {
	const [uploading, setUploading] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const { url: viewUrl, loading: urlLoading } = useSignedUrl(value);

	const hasFile = !!value;

	const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

	const handleFileChange = useCallback(
		async (e: ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (!file) return;

			if (file.size > MAX_FILE_SIZE) {
				notifications.error(
					`File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum size is ${MAX_FILE_SIZE / 1024 / 1024} MB.`,
				);
				if (inputRef.current) inputRef.current.value = "";
				return;
			}

			// Validate file type against the accept prop
			if (accept) {
				const allowedExts = accept
					.split(",")
					.map((s) => s.trim().toLowerCase());
				const fileExt = `.${file.name.split(".").pop()?.toLowerCase()}`;
				if (!allowedExts.includes(fileExt)) {
					notifications.error(
						`File type not allowed. Accepted formats: ${accept}`,
					);
					if (inputRef.current) inputRef.current.value = "";
					return;
				}
			}

			setUploading(true);
			try {
				const result = await uploadFileToS3(file);
				onUpload(result.key);
			} catch (err) {
				const message = err instanceof Error ? err.message : "Upload failed";
				notifications.error(message);
			} finally {
				setUploading(false);
				// Reset input so the same file can be re-selected
				if (inputRef.current) inputRef.current.value = "";
			}
		},
		[onUpload],
	);

	const handleView = useCallback(() => {
		if (!viewUrl) return;
		window.open(viewUrl, "_blank", "noopener,noreferrer");
	}, [viewUrl]);

	// Compact variant: no card wrapper, just the upload/success inline block
	if (compact) {
		return (
			<div>
				{uploading ? (
					<div className="flex items-center gap-2 rounded-sm border border-(--color-border) bg-(--color-muted) px-4 py-3">
						<Loader2
							size={18}
							className="animate-spin text-(--color-muted-foreground)"
						/>
						<span className="text-(--color-muted-foreground) text-sm">
							Uploading...
						</span>
					</div>
				) : !hasFile ? (
					<>
						<input
							ref={inputRef}
							type="file"
							id={id}
							accept={accept}
							onChange={handleFileChange}
							className="hidden"
						/>
						<label
							htmlFor={id}
							className="inline-flex cursor-pointer items-center gap-2 rounded-sm border border-(--color-border) bg-(--color-muted) px-[18px] py-2.5 font-medium text-(--color-muted-foreground) text-sm transition-all hover:bg-(--color-border) hover:text-(--color-foreground)"
						>
							<Upload size={16} />
							Choose File
						</label>
					</>
				) : (
					<div className="flex items-center justify-between rounded-sm border border-(--color-success-300) bg-(--color-success-50) px-4 py-3">
						<div className="flex items-center gap-2 font-medium text-(--color-success-700) text-sm">
							<Check size={18} />
							<span>Document uploaded</span>
							{(isS3Key(value) || isDataUrl(value)) && (
								<button
									type="button"
									onClick={handleView}
									disabled={urlLoading}
									className="ml-1 inline-flex items-center gap-1 text-(--color-primary-600) underline-offset-2 hover:underline disabled:opacity-50"
								>
									<ExternalLink size={14} />
									<span className="text-xs">View</span>
								</button>
							)}
						</div>
						<button
							type="button"
							className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-[6px] border border-red-200 bg-transparent text-(--color-error-500) transition-all hover:scale-105 hover:border-(--color-error-500) hover:bg-(--color-error-50)"
							onClick={onRemove}
							aria-label="Remove file"
						>
							<X size={16} />
						</button>
					</div>
				)}
			</div>
		);
	}

	// Full card variant (matches the Step1CompanyBasics "Supporting Documents" pattern)
	return (
		<div className="rounded-md border border-(--color-border) bg-(--color-muted) p-5 transition-all hover:shadow-sm">
			<div className="mb-2 flex items-center justify-between">
				<h4 className="font-semibold text-(--color-foreground) text-[15px]">
					{label}
				</h4>
				{required && (
					<span className="rounded border border-(--color-error-200) bg-(--color-error-50) px-2 py-0.5 font-semibold text-(--color-error-500) text-[11px] uppercase tracking-wide">
						Required
					</span>
				)}
			</div>
			{description && (
				<p className="mb-4 text-(--color-muted-foreground) text-[13px] leading-relaxed">
					{description}
				</p>
			)}
			<div className="mt-3">
				{uploading ? (
					<div className="flex items-center gap-2 rounded-sm border border-(--color-border) bg-(--color-muted) px-4 py-3">
						<Loader2
							size={18}
							className="animate-spin text-(--color-muted-foreground)"
						/>
						<span className="text-(--color-muted-foreground) text-sm">
							Uploading...
						</span>
					</div>
				) : !hasFile ? (
					<>
						<input
							ref={inputRef}
							type="file"
							id={id}
							accept={accept}
							onChange={handleFileChange}
							className="hidden"
						/>
						<label
							htmlFor={id}
							className="inline-flex cursor-pointer items-center gap-2 rounded-sm border border-(--color-border) bg-(--color-muted) px-[18px] py-2.5 font-medium text-(--color-muted-foreground) text-sm transition-all hover:bg-(--color-border) hover:text-(--color-foreground)"
						>
							<Upload size={18} />
							<span>Choose File</span>
						</label>
					</>
				) : (
					<div className="flex items-center justify-between rounded-sm border border-(--color-success-300) bg-(--color-success-50) px-4 py-3">
						<div className="flex items-center gap-2 font-medium text-(--color-success-700) text-sm">
							<Check size={18} />
							<span>Document uploaded</span>
							{(isS3Key(value) || isDataUrl(value)) && (
								<button
									type="button"
									onClick={handleView}
									disabled={urlLoading}
									className="ml-1 inline-flex items-center gap-1 text-(--color-primary-600) underline-offset-2 hover:underline disabled:opacity-50"
								>
									<ExternalLink size={14} />
									<span className="text-xs">View</span>
								</button>
							)}
						</div>
						<button
							type="button"
							className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-[6px] border border-red-200 bg-transparent text-(--color-error-500) transition-all hover:scale-105 hover:border-(--color-error-500) hover:bg-(--color-error-50)"
							onClick={onRemove}
							aria-label="Remove file"
						>
							<X size={16} />
						</button>
					</div>
				)}
			</div>
		</div>
	);
}
